import { ApActionType } from '../enums/ApActionType';
import { ApActionBase, ApPlanBase } from '../types/ActionPlan';
import { ApPlanAction, buildCreateApPlanAction } from '../types/ApPlanAction';
import { ApTemplateAction, buildCreateApTemplateAction } from '../types/ApTemplateAction';
import { getParentAction, getScopedActions, modifyPlanActions } from '../utils/actionPlanHelpers';

export interface ApLocation {
    title: string;
    actionIndex: number;
}

interface ApEditorState {
    plan: ApPlanBase;
    scopedActions: ApActionBase[];
    location: ApLocation[];
    editIndex: number;
}

export type ApEditorActions =
    | {
          type: 'SET_EDITOR_PLAN';
          plan: ApPlanBase;
          location: ApLocation[];
      }
    | {
          type: 'CREATE_TEMPLATE_ACTION';
          actionType: ApActionType;
      }
    | {
          type: 'CREATE_PLAN_ACTION';
          actionType: ApActionType;
          contactId: number;
          assignedUser: string;
      }
    | {
          type: 'MODIFY_EDITOR_ACTION';
          action: ApActionBase;
          actionIndex: number;
      }
    | {
          type: 'REMOVE_EDITOR_ACTION';
          actionIndex: number;
      }
    | {
          type: 'SET_EDITOR_EDIT';
          setEdit: boolean;
          actionIndex: number;
      }
    | {
          type: 'SET_EDITOR_LOCATION_FORWARD';
          nextLocation: ApLocation;
      }
    | {
          type: 'SET_EDITOR_LOCATION_BACK';
          nextLocation: ApLocation[];
      }
    | {
          type: 'REORDER_EDITOR_ACTION';
          actionIndex: number;
          direction: 'up' | 'down';
      };

export type ApEditorReducer = (state: ApEditorState, action: ApEditorActions) => ApEditorState;

export function actionPlanEditorReducer(
    state: ApEditorState,
    action: ApEditorActions,
): ApEditorState {
    switch (action.type) {
        case 'SET_EDITOR_PLAN':
            return {
                ...state,
                ...{
                    plan: action.plan,
                    scopedActions: action.plan?.actions || [],
                    location: action.location,
                    editIndex: -1,
                },
            };
        case 'CREATE_TEMPLATE_ACTION':
            const newTemplateAction = buildCreateApTemplateAction({
                type: action.actionType,
                parentId:
                    getParentAction(state.plan.actions, state.location)?.actionId || undefined,
                sort: state.scopedActions.length,
            }) as ApTemplateAction;

            const addedTemplateActions = [...state.scopedActions, newTemplateAction];

            return {
                ...state,
                ...{
                    scopedActions: addedTemplateActions,
                    editIndex: addedTemplateActions.length - 1,
                },
            };
        case 'CREATE_PLAN_ACTION':
            const newPlanAction = buildCreateApPlanAction({
                type: action.actionType,
                parentId:
                    getParentAction(state.plan.actions, state.location)?.actionId || undefined,
                assignedUser: action.assignedUser,
                contactId: action.contactId,
            }) as ApPlanAction;

            const addedPlanActions = [
                ...state.scopedActions.filter(act => act.actionId),
                newPlanAction,
            ];

            return {
                ...state,
                ...{
                    scopedActions: addedPlanActions,
                    editIndex: addedPlanActions.length - 1,
                },
            };
        case 'MODIFY_EDITOR_ACTION':
            const modifiedScopedActions = state.scopedActions.map((act, idx) =>
                idx === action.actionIndex ? action.action : act,
            );

            return {
                ...state,
                ...{
                    plan: {
                        ...state.plan,
                        ...{
                            actions: modifyPlanActions(
                                state.plan.actions,
                                state.location,
                                modifiedScopedActions,
                            ),
                        },
                    },
                    scopedActions: modifiedScopedActions,
                    editIndex: -1,
                },
            };
        case 'REMOVE_EDITOR_ACTION':
            const removedScopedActions = state.scopedActions.filter(
                (act, idx) => idx !== action.actionIndex,
            );

            return {
                ...state,
                ...{
                    plan: {
                        ...state.plan,
                        ...{
                            actions: modifyPlanActions(
                                state.plan.actions,
                                state.location,
                                removedScopedActions,
                            ),
                        },
                    },
                    scopedActions: removedScopedActions,
                    editIndex: -1,
                },
            };
        case 'SET_EDITOR_EDIT':
            const cancelledActions = state.scopedActions.filter(act => act.actionId);

            return {
                ...state,
                ...{
                    editIndex: action.actionIndex,
                    scopedActions: cancelledActions,
                },
            };
        case 'SET_EDITOR_LOCATION_FORWARD':
            const locationForward = [...state.location, action.nextLocation];

            return {
                ...state,
                ...{
                    location: locationForward,
                    scopedActions: getScopedActions(state.plan.actions, locationForward),
                    editIndex: -1,
                },
            };
        case 'SET_EDITOR_LOCATION_BACK':
            return {
                ...state,
                ...{
                    location: action.nextLocation,
                    scopedActions: getScopedActions(state.plan.actions, action.nextLocation),
                    editIndex: -1,
                },
            };
        case 'REORDER_EDITOR_ACTION':
            const endIndex = action.actionIndex + (action.direction === 'up' ? -1 : 1);
            const reorderAction = [...state.scopedActions];
            reorderAction.splice(endIndex, 0, reorderAction.splice(action.actionIndex, 1)[0]);
            reorderAction.forEach((itm, idx) => ((itm as ApTemplateAction).sort = idx));

            return {
                ...state,
                ...{
                    plan: {
                        ...state.plan,
                        ...{
                            title: state.plan.title,
                            actions: modifyPlanActions(
                                state.plan.actions,
                                state.location,
                                reorderAction,
                            ),
                        },
                    },
                    scopedActions: reorderAction,
                },
            };
        default:
            return state;
    }
}
